import React, { memo } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Chart colors
const CHART_COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  orange: '#F97316'
};

const TrendChartComponent = memo(({ 
  data, 
  title, 
  dataKey, 
  color = CHART_COLORS.primary, 
  loading = false,
  chartType = 'area', // 'area' or 'line'
  showGrid = true,
  showLegend = false,
  strokeWidth = 2,
  fillOpacity = 0.6,
  tooltipFormatter = null,
  customTooltip = null,
  multipleDataKeys = null, // For multiple lines/areas
  className = '',
  height = 300
}) => {
  const { t } = useTranslation();

  // Default tooltip formatter
  const defaultTooltipFormatter = tooltipFormatter || ((value, name) => [value, name]);

  // Render chart based on type
  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    if (chartType === 'line') {
      return (
        <LineChart {...commonProps}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis dataKey="name" />
          <YAxis />
          {customTooltip ? (
            <Tooltip content={customTooltip} />
          ) : (
            <Tooltip formatter={defaultTooltipFormatter} />
          )}
          {showLegend && <Legend />}
          {multipleDataKeys ? (
            multipleDataKeys.map((item, index) => (
              <Line
                key={item.key}
                type="monotone"
                dataKey={item.key}
                stroke={item.color || color}
                strokeWidth={strokeWidth}
                name={item.name || item.key}
              />
            ))
          ) : (
            <Line 
              type="monotone" 
              dataKey={dataKey} 
              stroke={color} 
              strokeWidth={strokeWidth}
              name={dataKey}
            />
          )}
        </LineChart>
      );
    }

    // Default to area chart
    return (
      <AreaChart {...commonProps}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" />}
        <XAxis dataKey="name" />
        <YAxis />
        {customTooltip ? (
          <Tooltip content={customTooltip} />
        ) : (
          <Tooltip formatter={defaultTooltipFormatter} />
        )}
        {showLegend && <Legend />}
        {multipleDataKeys ? (
          multipleDataKeys.map((item, index) => (
            <Area
              key={item.key}
              type="monotone"
              dataKey={item.key}
              stroke={item.color || color}
              fill={item.color || color}
              fillOpacity={fillOpacity}
              name={item.name || item.key}
            />
          ))
        ) : (
          <Area 
            type="monotone" 
            dataKey={dataKey} 
            stroke={color} 
            fill={color} 
            fillOpacity={fillOpacity}
            name={dataKey}
          />
        )}
      </AreaChart>
    );
  };

  return (
    <div className={`card ${className}`}>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      {loading || !data || data.length === 0 ? (
        <div className="flex items-center justify-center" style={{ height: `${height}px` }}>
          {loading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
            </div>
          ) : (
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.noDataAvailable')}</p>
            </div>
          )}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {renderChart()}
        </ResponsiveContainer>
      )}
    </div>
  );
});

TrendChartComponent.displayName = 'TrendChartComponent';

export default TrendChartComponent;
